import { IConfigSeller } from '@/models/configSeller'
import { IUser } from '@/models/user.model'
import { webApiService } from '@/services'
import { useAppDispatch } from '@/store/store'
import { updateUser } from '@/store/user'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

/**
 * Estado de la sucursal en la sesión federada de WOM, compartido por el modal de ingreso
 * (SucursalGate) y el selector del header: los dos leen las mismas sucursales de la
 * aserción SAML y resuelven el nombre elegido con el mismo endpoint.
 */
export const useSucursalSso = () => {
  const dispatch = useAppDispatch()
  const userState = useSelector((store: { user: IUser }) => store.user)
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const { pathname } = useLocation()
  const [resolviendo, setResolviendo] = useState(false)
  const [error, setError] = useState('')

  // El layout es una ruta sin path, así que useParams no trae el :slug: se lee de la URL,
  // igual que lo mandan el resto de las llamadas (getStoreRegions, getStoresByRegion...).
  const slug = pathname.split('/')[1] ?? ''
  const esWom = configSeller.config?.s3?.FOLDER_APP === 'ti_wom'
  const sucursales = useMemo(
    () => (esWom ? (userState.sucursales ?? []) : []),
    [esWom, userState.sucursales]
  )
  // Una selección que ya no está en la lista (le cambiaron las sucursales en WOM) no vale:
  // se vuelve a preguntar. Sin id_store resuelto tampoco sirve.
  const seleccionada = sucursales.includes(String(userState.sucursal)) ? String(userState.sucursal) : ''
  const pendiente = seleccionada === '' || Number(userState.id_store) <= 0

  const resolver = useCallback(async (nombre: string): Promise<void> => {
    setResolviendo(true)
    setError('')
    try {
      const res = await webApiService.getStoreByName(nombre, slug)
      const fila = Array.isArray(res?.data) ? res.data[0] : res?.data
      const idStore = Number(fila?.id_store ?? fila?.id ?? 0)
      if (!Number.isFinite(idStore) || idStore <= 0) throw new Error(`getStoreByName no devolvió id_store para "${nombre}"`)
      // `selectedStore` es el mismo id, pero es el campo que leen los listados para
      // filtrar por tienda: al escribirlo, el getList se dispara de nuevo con la sucursal.
      dispatch(updateUser({ sucursal: nombre, id_store: idStore, selectedStore: idStore }))
    } catch (err) {
      console.error('No se pudo resolver la sucursal:', err)
      setError('No pudimos identificar esa sucursal. Reintenta o avisa a soporte.')
    } finally {
      setResolviendo(false)
    }
  }, [dispatch, slug])

  return { esWom, sucursales, seleccionada, pendiente, resolviendo, error, setError, resolver }
}
